/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable no-param-reassign */

import { Grid, makeStyles, Typography } from '@material-ui/core';
import { Lock } from '@material-ui/icons';
import Button from 'components/Button';
import Loading from 'components/Loading';
import { Formik } from 'formik';
import i18n from 'i18n';
import { getSecurityStateRequest } from 'modules/environments/actions';
import {
  DeviceValidationSettings,
  SecurityState,
  ZeroTrustProps,
} from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import GenericForm from 'skyone-lib/lib/GenericForm';
import ApplicationState from 'store/interfaces';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import { applyI18n, controlMaxAndMinValues } from 'utils/helpers';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import clsx from 'clsx';
import { modalOpen, modalClose } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import validationSchema from './schemaValidator';
import { passwordFields } from './formFields';
import { initial } from './securityValues';
import styles from './styles';
import ZeroTrustCard from './ZeroTrustCard';
import DeviceValidationCard from './DeviceValidationCard';

const useStyles = makeStyles(styles);

interface Props {
  companySelected: CompanyData | null;
  isLoading: boolean;
  role?: RolesEnum;
  securityState: SecurityState;
  zeroTrustValues?: ZeroTrustProps;
  deviceValidationValues?: DeviceValidationSettings;
  getSecurityStateRequest: (id: string) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  onSubmit: (props: {
    securityValues: any;
    zeroTrustValues: any;
    deviceValidationValues: any;
  }) => void;
}

const FormSecurity = ({
  companySelected,
  isLoading,
  role,
  securityState,
  zeroTrustValues,
  deviceValidationValues,
  getSecurityStateRequest,
  modalClose,
  modalOpen,
  onSubmit,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [hasError, setHasError] = React.useState(false);

  const handleSubmit = ({
    zero_trust,
    device_validation_settings,
    ...rest
  }) => {
    if (zeroTrustValues?.enabled !== zero_trust?.enabled) {
      modalOpen({
        title: zero_trust?.enabled
          ? i18n.t('WANT_ACTIVATE_ZERO_TRUST_KEY')
          : i18n.t('WANT_DEACTIVATE_ZERO_TRUST_KEY'),
        content: zero_trust?.enabled
          ? i18n.t('ACTIVATE_ZERO_TRUST_DESCRIPTION_KEY')
          : i18n.t('DEACTIVATE_ZERO_TRUST_DESCRIPTION_KEY'),
        footer: (
          <Grid>
            <Button
              submitButton
              onClick={() => {
                onSubmit({
                  securityValues: rest,
                  zeroTrustValues: zero_trust,
                  deviceValidationValues: device_validation_settings,
                });
                modalClose();
              }}
            >
              {i18n.t('CONFIRM_KEY')}
            </Button>
            <Button id="cancel-button" variant="outlined" onClick={modalClose}>
              {i18n.t('CANCEL_KEY')}
            </Button>
          </Grid>
        ),
      });
    } else {
      onSubmit({
        securityValues: rest,
        zeroTrustValues: zero_trust,
        deviceValidationValues: device_validation_settings,
      });
    }
  };

  React.useEffect(() => {
    getSecurityStateRequest(id);
  }, [getSecurityStateRequest, id]);

  return isLoading ? (
    <Loading />
  ) : (
    <Formik
      initialValues={{
        ...initial,
        ...securityState,
        zero_trust: zeroTrustValues,
        device_validation_settings: deviceValidationValues,
      }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ setFieldValue, errors, values, submitForm }) => (
        <Grid container spacing={2}>
          <Grid container xs={12} spacing={2} className={classes.container}>
            <Grid item xs={6}>
              <Grid item className={classes.passwordRules}>
                <Grid className={classes.titleHeader}>
                  <Lock
                    className={clsx(classes.iconLock, {
                      [classes.iconLockAdmin]: inAdminView(
                        role,
                        companySelected
                      ),
                      [classes.iconLockSuperAdmin]: inSuperAdminView(
                        role,
                        companySelected
                      ),
                      [classes.iconLockOperator]: inOperatorView(
                        role,
                        companySelected
                      ),
                    })}
                  />
                  <Grid className={classes.title}>
                    <Typography variant="h6">
                      {i18n.t('USE_SPECIFIC_PASSWORD_KEY')}
                    </Typography>
                    <Typography className={classes.subtitle}>
                      {securityState?.activate
                        ? i18n.t('ENABLED_KEY')
                        : i18n.t('DISABLED_KEY')}
                    </Typography>
                  </Grid>
                </Grid>
                <section className={classes.content}>
                  <Grid>
                    <GenericForm
                      fields={values}
                      screenFields={applyI18n(
                        passwordFields.slice(0, 1),
                        i18n.language
                      )}
                      setFieldValue={setFieldValue}
                      errors={errors}
                    />
                    <Grid container className={classes.rulesPassword}>
                      <GenericForm
                        fields={values}
                        screenFields={applyI18n(
                          passwordFields.slice(1, 5),
                          i18n.language
                        )}
                        setFieldValue={setFieldValue}
                        errors={errors}
                      />
                    </Grid>
                    <GenericForm
                      fields={values}
                      screenFields={applyI18n(
                        passwordFields.slice(8, 9),
                        i18n.language
                      )}
                      setFieldValue={setFieldValue}
                      errors={errors}
                    />
                    <GenericForm
                      fields={values}
                      screenFields={applyI18n(
                        passwordFields.slice(9, 10),
                        i18n.language
                      )}
                      setFieldValue={setFieldValue}
                      errors={errors}
                    />
                    <GenericForm
                      fields={values}
                      screenFields={applyI18n(
                        passwordFields.slice(5, 6),
                        i18n.language
                      )}
                      setFieldValue={setFieldValue}
                      errors={errors}
                    />
                  </Grid>
                  {values.expire_passwords && (
                    <Grid className={classes.passwordExpire}>
                      <GenericForm
                        fields={values}
                        screenFields={applyI18n(
                          passwordFields.slice(6, 7),
                          i18n.language
                        )}
                        setFieldValue={setFieldValue}
                        errors={errors}
                      />
                    </Grid>
                  )}
                  <Grid className={classes.passwordExpire}>
                    <GenericForm
                      fields={{
                        ...values,
                        max_auth_tries: controlMaxAndMinValues(
                          values.max_auth_tries,
                          {
                            ...(Number(securityState?.max_auth_tries) > 0 && {
                              min: 1,
                            }),
                          }
                        ),
                      }}
                      screenFields={applyI18n(
                        passwordFields.slice(7, 8).map(item => {
                          values.max_auth_tries = controlMaxAndMinValues(
                            values.max_auth_tries,
                            {
                              ...(Number(securityState?.max_auth_tries) > 0 && {
                                min: 1,
                              }),
                            }
                          );
                          return {
                            ...item,
                          };
                        }),
                        i18n.language
                      )}
                      setFieldValue={setFieldValue}
                      errors={errors}
                    />
                  </Grid>
                </section>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <ZeroTrustCard
                zeroTrustValues={values?.zero_trust}
                setFieldValue={setFieldValue}
                setHasError={setHasError}
                errors={errors}
              />
            </Grid>
            <Grid item xs={6}>
              <DeviceValidationCard
                deviceValidationValues={deviceValidationValues}
                setFieldValue={setFieldValue}
              />
            </Grid>
          </Grid>

          <Grid container justify="flex-end" className={classes.buttons}>
            <Button
              id="submit-button"
              submitButton
              onClick={submitForm}
              disabled={hasError}
              isLoading={isLoading}
            >
              {i18n.t('SAVE_KEY')}
            </Button>
            <Button
              id="cancel-button"
              variant="outlined"
              onClick={() => history.push('/environments')}
            >
              {i18n.t('CANCEL_KEY')}
            </Button>
          </Grid>
        </Grid>
      )}
    </Formik>
  );
};

const mapStateToProps = ({
  environments: {
    securityState,
    environmentSelected,
    isLoading,
    isLoadingSecurityState,
  },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  securityState,
  zeroTrustValues: environmentSelected?.zero_trust,
  deviceValidationValues: environmentSelected?.device_validation_settings,
  isLoading: isLoading || isLoadingSecurityState,
  companySelected,
  role: data?.role,
});

export default connect(mapStateToProps, {
  getSecurityStateRequest,
  modalClose,
  modalOpen,
})(FormSecurity);
